/**
 * Asset Loader
 * 
 * Phase 6.0 Track D: CDN-backed asset loading with local fallback
 */

import type { GameManifestV1 } from "./types";

/**
 * Get base URL for runtime assets
 * 
 * Priority:
 * 1. VITE_RUNTIME_ASSETS_BASE_URL env var (for CDN)
 * 2. Local /runtime-assets (for dev)
 */
export function getRuntimeAssetsBaseUrl(): string {
  // Check for CDN URL in env
  const envBaseUrl = import.meta.env.VITE_RUNTIME_ASSETS_BASE_URL;
  if (envBaseUrl) {
    return envBaseUrl.endsWith("/") ? envBaseUrl.slice(0, -1) : envBaseUrl;
  }

  // Default to local backend
  return import.meta.env.VITE_RUNTIME_ASSETS_URL || "/runtime-assets";
}

/**
 * Load game manifest from CDN or local
 */
export async function loadGameManifest(
  gameKey: string,
  version: string
): Promise<GameManifestV1> {
  const baseUrl = getRuntimeAssetsBaseUrl();
  const manifestUrl = `${baseUrl}/games/${gameKey}/${version}/manifest.json`;

  console.log(`[AssetLoader] Loading manifest: ${manifestUrl}`);

  try {
    const response = await fetch(manifestUrl);

    if (!response.ok) {
      throw new Error(`Failed to load manifest: ${response.status} ${response.statusText}`);
    }

    const manifest = await response.json();

    // Basic validation
    if (!manifest.version || !manifest.gameKey || !manifest.gameVersion) {
      throw new Error("Invalid manifest structure");
    }

    console.log(`[AssetLoader] Loaded manifest for ${gameKey} v${version}`);
    return manifest as GameManifestV1;
  } catch (error) {
    console.error(`[AssetLoader] Failed to load manifest:`, error);
    throw error;
  }
}

/**
 * Preload assets (images, fonts, etc.)
 */
export async function preloadAssets(manifest: GameManifestV1): Promise<void> {
  const baseUrl = getRuntimeAssetsBaseUrl();
  const promises: Promise<void>[] = [];

  // Preload images
  if (manifest.assets.images && manifest.assets.images.length > 0) {
    manifest.assets.images.forEach((imagePath) => {
      const imageUrl = imagePath.startsWith("http")
        ? imagePath
        : `${baseUrl}${imagePath}`;

      const promise = new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
        img.src = imageUrl;
      });

      promises.push(promise);
    });
  }

  // Preload sounds
  if (manifest.assets.sounds && manifest.assets.sounds.length > 0) {
    manifest.assets.sounds.forEach((soundPath) => {
      const soundUrl = soundPath.startsWith("http")
        ? soundPath
        : `${baseUrl}${soundPath}`;

      const promise = fetch(soundUrl).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load sound: ${soundUrl}`);
        }
      });

      promises.push(promise);
    });
  }

  await Promise.all(promises);
  console.log(`[AssetLoader] Preloaded ${promises.length} assets`);
}
